// src/pages/billing/PricingPage.jsx
// Premium pricing page aligned with login/landing glassmorphism design

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { billingService } from "../../api/billingService";
import AgentBlaynaLogo from "../../assets/Agent-Blayna-Logo.png";

// ── Design tokens (match login/landing) ──────────────────────────────
const c = {
  ink: "#0B0F1A",
  subtle: "rgba(11, 15, 26, 0.55)",
  muted: "rgba(11, 15, 26, 0.40)",
  border: "rgba(11, 15, 26, 0.10)",
  blue: "#2D5BFF",
  blueSoft: "rgba(45, 91, 255, 0.08)",
  white82: "rgba(255, 255, 255, 0.82)",
  white95: "rgba(255, 255, 255, 0.95)",
  success: "#15803d",
  successBg: "#ecfdf5",
};

const gradient = `
  radial-gradient(1100px circle at 16% 0%, rgba(45, 91, 255, 0.15), transparent 55%),
  radial-gradient(1100px circle at 88% 12%, rgba(122, 92, 255, 0.10), transparent 60%),
  radial-gradient(1200px circle at 50% 110%, rgba(0, 0, 0, 0.06), transparent 60%),
  linear-gradient(180deg, #F7F8FA 0%, #F3F4F7 40%, #F7F8FA 100%)
`;

const grain = {
  backgroundImage:
    "radial-gradient(circle at 1px 1px, rgba(0,0,0,.35) 1px, transparent 0)",
  backgroundSize: "20px 20px",
};

// ── Plan metadata ────────────────────────────────────────────────────
const PLAN_FEATURES = {
  starter: [
    "1 team member",
    "Unlimited clients",
    "AI-powered insights",
    "Commission tracking",
    "Email support",
  ],
  team: [
    "5 team members",
    "Everything in Starter",
    "Team collaboration",
    "Shared client database",
    "Priority support",
  ],
  growth: [
    "10 team members",
    "Everything in Team",
    "Advanced analytics",
    "Custom integrations",
    "Dedicated support",
  ],
};

const PLAN_PRICES_FALLBACK = { starter: 29, team: 79, growth: 149 };

const DEFAULT_PLANS = [
  { name: "starter", display_name: "Starter", price: 29, seat_limit: 1, description: "Perfect for solo agents" },
  { name: "team", display_name: "Team", price: 79, seat_limit: 5, recommended: true, description: "For small teams up to 5 agents" },
  { name: "growth", display_name: "Growth", price: 149, seat_limit: 10, description: "For growing brokerages" },
];

// ── Component ────────────────────────────────────────────────────────
export default function PricingPage() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");
  const isSmall = useMediaQuery("(max-width:600px)");

  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await billingService.getPlans();
        const apiPlans = data.plans || data;
        if (Array.isArray(apiPlans) && apiPlans.length > 0) {
          const enriched = apiPlans.map((p) => ({
            ...p,
            price: p.price_amount ? p.price_amount / 100 : PLAN_PRICES_FALLBACK[p.name] || 0,
            features: p.features || PLAN_FEATURES[p.name] || [],
            recommended: p.is_popular || p.name === "team",
            billingInterval: p.billing_interval || "month",
          }));
          setPlans(enriched);
        }
      } catch {
        // Keep defaults
      } finally {
        setPlansLoading(false);
      }
    };

    const fetchStatus = async () => {
      try {
        const status = await billingService.getStatus();
        setSubscriptionStatus(status);
      } catch {
        // Ignore
      }
    };

    fetchPlans();
    fetchStatus();
  }, []);

  const isTrialUser = subscriptionStatus?.trial_active && !subscriptionStatus?.has_subscription;

  const getButtonText = (planName) => {
    if (!subscriptionStatus) return "Start Free Trial";
    if (subscriptionStatus.subscription_plan === planName) return "Current Plan";
    if (isTrialUser) return "Subscribe Now";
    if (subscriptionStatus.has_subscription) return "Change Plan";
    return "Start Free Trial";
  };

  const handleSubscribe = async (planName) => {
    setLoading(planName);
    setError("");
    try {
      const { url } = await billingService.createCheckout(planName);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      setError(err.message || "Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const isCurrent = (planName) => subscriptionStatus?.subscription_plan === planName;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        padding: isSmall ? "16px 12px" : "32px 24px",
        overflow: "hidden",
      }}
    >
      {/* ── Background gradient ── */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: gradient }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.05, ...grain }} />

      {/* ── Back button (pill, like login) ── */}
      <div
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: isSmall ? 16 : 24,
          left: isSmall ? 16 : 24,
          zIndex: 10,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 14px",
          borderRadius: 999,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          border: `1px solid ${c.border}`,
          fontSize: 13,
          fontWeight: 600,
          color: "rgba(11, 15, 26, 0.65)",
          cursor: "pointer",
          transition: "background-color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
          e.currentTarget.style.color = c.ink;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.75)";
          e.currentTarget.style.color = "rgba(11, 15, 26, 0.65)";
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>&larr;</span>
        Back
      </div>

      {/* ── Header ── */}
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", marginTop: isSmall ? 56 : 48, marginBottom: 36 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <img src={AgentBlaynaLogo} alt="AgentBlayna" style={{ height: 36, width: "auto" }} />
          <span style={{ fontSize: 15, fontWeight: 900, letterSpacing: -0.3, color: c.ink }}>AgentBlayna</span>
        </div>

        <h1 style={{
          fontSize: isSmall ? 28 : 36,
          fontWeight: 900,
          letterSpacing: "-1.5px",
          lineHeight: 1.1,
          margin: "0 0 10px",
          color: c.ink,
        }}>
          {isTrialUser ? "Upgrade Your Plan" : "Choose Your Plan"}
        </h1>

        {!isTrialUser && (
          <p style={{ margin: "0 0 14px", fontSize: 15, color: c.subtle, maxWidth: 420, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
            Start with a 7-day free trial. No credit card required. Cancel anytime.
          </p>
        )}

        {!isTrialUser && (
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 14px",
            borderRadius: 999,
            backgroundColor: c.successBg,
            border: "1px solid rgba(21, 128, 61, 0.15)",
            fontSize: 12.5,
            fontWeight: 700,
            color: c.success,
          }}>
            <span style={{ fontSize: 14 }}>&#9733;</span>
            7-Day Free Trial Included
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          position: "relative", zIndex: 1, maxWidth: 520, width: "100%",
          backgroundColor: "#fef2f2", color: "#b91c1c", padding: "10px 14px",
          borderRadius: 10, fontSize: 13, marginBottom: 20, border: "1px solid #fecaca",
          textAlign: "center",
        }}>
          {error}
        </div>
      )}

      {/* ── Plan cards ── */}
      {plansLoading ? (
        <div style={{ position: "relative", zIndex: 1, padding: "60px 0" }}>
          <CircularProgress size={32} sx={{ color: c.blue }} />
        </div>
      ) : (
        <div style={{
          position: "relative",
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: isSmall ? 16 : 20,
          maxWidth: 960,
          width: "100%",
          alignItems: "stretch",
        }}>
          {plans.map((plan) => {
            const featured = plan.recommended;
            const current = isCurrent(plan.name);
            const seatLimit = plan.seat_limit || plan.seats || 1;
            const features = plan.features || PLAN_FEATURES[plan.name] || [];

            return (
              <div
                key={plan.name}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 20,
                  padding: featured ? "2px" : 0,
                  background: featured
                    ? "linear-gradient(135deg, #2D5BFF 0%, #7A5CFF 100%)"
                    : "transparent",
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {/* "Most Popular" badge */}
                {featured && (
                  <div style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 2,
                    padding: "5px 14px",
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #2D5BFF 0%, #7A5CFF 100%)",
                    color: "#fff",
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 0.3,
                    whiteSpace: "nowrap",
                    boxShadow: "0 4px 14px rgba(45, 91, 255, 0.35)",
                  }}>
                    &#9733; Most Popular
                  </div>
                )}

                <div style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: featured ? 18 : 20,
                  backgroundColor: c.white82,
                  backdropFilter: "blur(18px) saturate(180%)",
                  WebkitBackdropFilter: "blur(18px) saturate(180%)",
                  border: featured ? "none" : `1px solid ${c.border}`,
                  boxShadow: featured
                    ? "0 20px 60px rgba(45, 91, 255, 0.12), 0 4px 12px rgba(11, 15, 26, 0.04)"
                    : "0 8px 30px rgba(11, 15, 26, 0.06), 0 2px 6px rgba(11, 15, 26, 0.02)",
                  padding: isSmall ? "28px 22px" : "32px 28px",
                  overflow: "hidden",
                }}>
                  {/* Plan name + description */}
                  <h3 style={{
                    margin: "0 0 4px",
                    fontSize: 20,
                    fontWeight: 900,
                    letterSpacing: -0.3,
                    color: c.ink,
                  }}>
                    {plan.display_name || plan.display || plan.name}
                  </h3>
                  <p style={{ margin: "0 0 20px", fontSize: 13.5, color: c.subtle, lineHeight: 1.5 }}>
                    {plan.description}
                  </p>

                  {/* Price */}
                  <div style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 38, fontWeight: 900, letterSpacing: -1.5, color: c.ink }}>
                      ${plan.price}
                    </span>
                    <span style={{ fontSize: 14, color: c.muted, fontWeight: 500, marginLeft: 2 }}>
                      /{plan.billingInterval === "year" ? "yr" : "mo"}
                    </span>
                  </div>

                  {/* Seats */}
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 10px",
                    borderRadius: 999,
                    backgroundColor: c.blueSoft,
                    fontSize: 12,
                    fontWeight: 600,
                    color: c.blue,
                    marginBottom: 22,
                    alignSelf: "flex-start",
                  }}>
                    <span style={{ fontSize: 13 }}>&#128101;</span>
                    {seatLimit === 1 ? "1 seat" : `${seatLimit} seats`}
                  </div>

                  {/* Features */}
                  <div style={{ display: "grid", gap: 10, marginBottom: 28, flexGrow: 1 }}>
                    {features.map((f) => (
                      <div key={f} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <span style={{
                          width: 18, height: 18, borderRadius: 999,
                          backgroundColor: c.successBg,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10.5, color: c.success, flexShrink: 0, marginTop: 1,
                        }}>
                          &#10003;
                        </span>
                        <span style={{ fontSize: 13.5, color: "rgba(11, 15, 26, 0.72)", lineHeight: 1.4 }}>
                          {f}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => !current && handleSubscribe(plan.name)}
                    disabled={loading !== null || current}
                    style={{
                      width: "100%",
                      padding: "12px 0",
                      borderRadius: 999,
                      border: current
                        ? `1px solid ${c.border}`
                        : featured ? "none" : `1px solid ${c.border}`,
                      background: current
                        ? "transparent"
                        : featured
                          ? c.ink
                          : "transparent",
                      color: current
                        ? c.muted
                        : featured
                          ? "#fff"
                          : c.ink,
                      fontSize: 14,
                      fontWeight: 800,
                      cursor: current || loading ? "default" : "pointer",
                      opacity: loading && loading !== plan.name ? 0.5 : 1,
                      boxShadow: featured && !current ? "0 12px 36px rgba(11, 15, 26, 0.14)" : "none",
                      transition: "transform 0.08s ease, box-shadow 0.08s ease, background-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (current) return;
                      if (!featured) {
                        e.currentTarget.style.backgroundColor = "rgba(11, 15, 26, 0.04)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (current) return;
                      if (!featured) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                    onMouseDown={(e) => {
                      if (current) return;
                      e.currentTarget.style.transform = "translateY(1px)";
                    }}
                    onMouseUp={(e) => {
                      if (current) return;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {loading === plan.name ? (
                      <CircularProgress size={18} sx={{ color: featured ? "#fff" : c.ink }} />
                    ) : (
                      getButtonText(plan.name)
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Footer ── */}
      <p style={{
        position: "relative", zIndex: 1,
        marginTop: 36, fontSize: 12.5, color: c.muted, textAlign: "center",
      }}>
        Questions? Contact us at support@agentblayna.com
      </p>
    </div>
  );
}
